// src/utils/capas.js
// ═══════════════════════════════════════════════════════════════════════════
// "Atrás" cierra lo que está encima (etapa 2 del enrutador).
//
// Una CAPA es cualquier cosa que se abre sobre la pantalla: hojas de MovilUI,
// ventanas con fondo oscuro, el menú deslizable del teléfono y el visor de PDF
// de la app instalada. Todas usan este único mecanismo:
//
//   · Abrir  = navigate a la MISMA dirección (push) con `state.capas` que lleva
//              la pila de capas abiertas. La dirección no cambia.
//   · Cerrar con la X, tocando fuera o por código = la capa se desmonta y aquí
//              se hace navigate(-1) para quitar su entrada del historial.
//   · Atrás  = el navegador hace pop; la capa ya no está en `location.state` y
//              se le pide cerrarse (su `onCerrar` de siempre).
//
// Reglas que cumple:
//   · Capas anidadas: cada "atrás" cierra solo la de arriba.
//   · Ir a otro módulo con capas abiertas (`navegarFuera`): las entradas de las
//     capas se reemplazan por la del módulo nuevo; no queda una entrada huérfana.
//   · Recargar con una capa abierta: la pantalla vuelve sin la capa y su
//     entrada (que el navegador conserva) se descarta.
//   · Nunca window.history.* directo: todo pasa por el navigate del enrutador.
//
// Para una capa nueva: dentro de su caja, <CapaAtras onCerrar={…} /> (o
// useCapa(abierta, cerrar) si la capa siempre está montada). `onCerrar` debe
// ser lo mismo que hace tocar fuera o «Cancelar».
// ═══════════════════════════════════════════════════════════════════════════

import { useEffect, useRef } from 'react';

let nav = null;          // navigate del enrutador (lo fija App en cada render)
let loc = null;          // location actual del enrutador
let seq = 0;

// id → { cerrar, empujada }. `empujada`: su entrada ya está en el historial.
const abiertas = new Map();

// Cola de operaciones. Un navigate(-n) es asíncrono (llega con el pop), así que
// lo que venga después espera a que llegue: si no, un push se colaría antes
// del pop y el pop cerraría la capa recién abierta.
const cola = [];
let esperandoPop = false;
let relojPop = null;

const pilaDe = (l) => (Array.isArray(l?.state?.capas) ? l.state.capas : []);
const destinoMismo = (l) => ({ pathname: l.pathname, search: l.search, hash: l.hash });

const idxHistorial = () => {
  try { const i = window.history.state?.idx; return typeof i === 'number' ? i : null; }
  catch { return null; }
};

function retroceder(n) {
  if (n <= 0) return;
  esperandoPop = true;
  const meta = idxHistorial() == null ? null : idxHistorial() - n;
  let intentos = 0;
  // Red de seguridad: si el aviso del pop no llegara, la cola no se queda
  // trabada. Solo se sigue cuando el historial ya está donde debía (un
  // navegador ocupado puede tardar más de lo normal en hacer el pop).
  const vigilar = () => {
    clearTimeout(relojPop);
    relojPop = setTimeout(() => {
      if (!esperandoPop) return;
      intentos += 1;
      if ((meta !== null && idxHistorial() === meta) || intentos >= 8) {
        esperandoPop = false;
        vaciarCola();
      } else {
        vigilar();
      }
    }, 1000);
  };
  vigilar();
  nav(-n);
}

function vaciarCola() {
  while (!esperandoPop && cola.length) {
    const op = cola.shift();
    try { op(); } catch (e) { console.error('capas:', e); }
  }
}

function encolar(op) {
  cola.push(op);
  vaciarCola();
}

// ── Lo llama App ────────────────────────────────────────────────────────────
// En el render (antes de que se desmonte nada): así una capa que se desmonta
// porque la pantalla cambió ya ve la ubicación nueva y no retrocede de más.
export function fijarEnrutador(location, navigate) {
  loc = location;
  nav = navigate;
}

// Después de cada cambio de ubicación (useLayoutEffect sobre location.key).
export function alCambiarUbicacion(location, tipo) {
  loc = location;

  if (esperandoPop) {
    esperandoPop = false;
    clearTimeout(relojPop);
  }

  // 1. Capas abiertas cuya entrada ya no está: se cerraron con "atrás".
  const pila = pilaDe(location);
  abiertas.forEach((c, id) => {
    if (c.empujada && !pila.includes(id)) {
      abiertas.delete(id);
      try { c.cerrar(); } catch (e) { console.error('capas:', e); }
    }
  });

  // 2. Entradas de capas que ya no existen (recargar con una capa abierta, o
  //    "adelante" hacia una capa ya cerrada): se descartan.
  if (tipo === 'POP' && pila.length && cola.length === 0) {
    let muertas = 0;
    for (let i = pila.length - 1; i >= 0 && !abiertas.has(pila[i]); i--) muertas++;
    if (muertas) {
      // Atrás, adelante o recargar: se sigue de largo hasta la última entrada
      // viva. "Adelante" hacia una capa ya cerrada no hace nada visible.
      retroceder(muertas);
      return;
    }
  }

  vaciarCola();
}

// ── Abrir / soltar (también desde código fuera de React: visor de PDF) ──────
export function abrirCapa(cerrar) {
  const id = `c${Date.now().toString(36)}-${++seq}`;
  abiertas.set(id, { cerrar, empujada: false });
  if (!nav) return id;   // sin enrutador (p. ej. admisión pública): capa sin historial
  encolar(() => {
    const c = abiertas.get(id);
    if (!c || !loc) return;              // se cerró antes de llegar a empujarse
    c.empujada = true;
    nav(destinoMismo(loc), { state: { ...(loc.state || {}), capas: [...pilaDe(loc), id] } });
  });
  return id;
}

// La capa se cerró por su cuenta (X, tocar fuera, guardar…): quitar su entrada.
export function soltarCapa(id) {
  const c = abiertas.get(id);
  if (!c) return;                        // ya la cerró "atrás"
  abiertas.delete(id);
  if (!c.empujada || !nav) return;
  encolar(() => {
    const pila = pilaDe(loc);
    const pos = pila.indexOf(id);
    if (pos === -1) return;              // la pantalla ya cambió
    if (pos === pila.length - 1) {
      // Arriba de todo: un paso atrás. Si debajo quedaron capas ya cerradas
      // (se cerró una de abajo antes que la de arriba), se quitan también.
      let n = 1;
      for (let i = pos - 1; i >= 0 && !abiertas.has(pila[i]); i--) n++;
      retroceder(n);
    }
    // Si no está arriba (se cerró una capa de abajo con otra encima), su
    // entrada se quita cuando se cierre la de arriba (ver el bucle de arriba).
  });
}

// ── Ir a otra pantalla con capas abiertas ───────────────────────────────────
// Las entradas de las capas se sustituyen por la del destino, para que un
// "atrás" posterior vuelva a la pantalla de antes y no a una capa huérfana.
export function navegarFuera(destino, opciones = {}) {
  if (!nav) return;
  encolar(() => {
    const k = pilaDe(loc).length;
    if (!k) { nav(destino, opciones); return; }
    const atras = opciones.replace ? k : k - 1;
    if (atras > 0) retroceder(atras);
    // Tras el pop (si hubo), reemplazar la entrada en la que se quedó.
    cola.unshift(() => nav(destino, { ...opciones, replace: true }));
    vaciarCola();
  });
}

// ── Registros con dirección propia (etapa 3) ────────────────────────────────
// ¿Hay capas abiertas encima de la pantalla actual?
export function hayCapasAbiertas() {
  return pilaDe(loc).length > 0;
}

// Un paso atrás en orden con la cola (cerrar un expediente que se abrió desde
// la lista: su entrada sale del historial, igual que la de una capa).
export function retrocederEnCola(n = 1) {
  if (!nav) return;
  encolar(() => retroceder(n));
}

// ── Para componentes ────────────────────────────────────────────────────────
// Registra una capa mientras `abierta` sea verdadero. `cerrar` es lo que hace
// la capa al tocar fuera / Cancelar. Si al pedirle cerrarse con "atrás" no se
// cierra (está guardando, por ejemplo), vuelve a ponerse en el historial.
export function useCapa(abierta, cerrar) {
  const cerrarRef = useRef(cerrar);
  cerrarRef.current = cerrar;
  useEffect(() => {
    if (!abierta) return undefined;
    let vivo = true;
    let id = null;
    const registrar = () => {
      id = abrirCapa(() => {
        id = null;
        if (cerrarRef.current) cerrarRef.current();
        // ¿Sigue abierta después de pedirle cerrarse? Vuelve a la pila.
        setTimeout(() => { if (vivo && id === null) registrar(); }, 0);
      });
    };
    // En un microtask: el doble montaje de StrictMode (desarrollo) monta y
    // desmonta en el mismo tick y así no deja entradas de más.
    Promise.resolve().then(() => { if (vivo) registrar(); });
    return () => {
      vivo = false;
      if (id) soltarCapa(id);
    };
  }, [abierta]);
}

// Marca invisible para poner dentro de cualquier capa: mientras esté montada,
// "atrás" la cierra. No pinta nada.
export function CapaAtras({ onCerrar }) {
  useCapa(true, onCerrar);
  return null;
}
